import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useState } from 'react';
import { useQuery } from 'urql';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import { CollectionComponent } from './CollectionComponent';
import { CollectionContext } from './context';
import { getCollections, getCollectionSansSwatches } from './queries';
import { OV_ALERT, TEXT } from '../../config/constants';
import categoryFilter from './filters/category';
import Collection from '../../components/Collection';
import CollectionLoading from '../../components/CollectionLoading';
import CollectionSet from '../../components/CollectionSet';
import css from './Collection.module.scss';
import ErrorMessage from '../../components/ErrorMessage';
import ExpandCollapser from '../../components/ExpandCollapser';
import fabricFilter from './filters/fabric';
import genderFilter from './filters/gender';
import Hero from '../../components/Hero';
import lengthFilter from './filters/length';
import paletteFilter from './filters/palette';
import priceFilter from './filters/price';
import RecommendationsAI from '../../components/RecommendationsAI';
import sizeFilter from './filters/size';
import supportFilter from './filters/support';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var filtersMap = {
  category: categoryFilter,
  fabric: fabricFilter,
  gender: genderFilter,
  length: lengthFilter,
  palette: paletteFilter,
  price: priceFilter,
  size: sizeFilter,
  support: supportFilter
};

var Wrap = function Wrap(_ref) {
  var banner = _ref.banner,
      children = _ref.children,
      gender = _ref.gender,
      pageTitle = _ref.pageTitle;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [banner && /*#__PURE__*/_jsx(Hero, _objectSpread(_objectSpread({}, banner), {}, {
      gender: gender,
      pageTitle: pageTitle
    })), children && children]
  });
};

Wrap.defaultProps = {
  banner: false,
  children: false,
  gender: false,
  pageTitle: false
};
Wrap.propTypes = {
  banner: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape()]),
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.node]),
  gender: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  pageTitle: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
};

var CollectionLoader = function CollectionLoader(_ref2) {
  var banner = _ref2.banner,
      displaySwatches = _ref2.displaySwatches,
      paletteParam = _ref2.paletteParam,
      shopifyId = _ref2.shopifyId;

  var _useQuery = useQuery({
    query: displaySwatches ? getCollections : getCollectionSansSwatches,
    variables: {
      shopifyId: shopifyId
    }
  }),
      _useQuery2 = _slicedToArray(_useQuery, 1),
      _useQuery2$ = _useQuery2[0],
      data = _useQuery2$.data,
      fetching = _useQuery2$.fetching,
      error = _useQuery2$.error;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      gender = _useState2[0],
      setGender = _useState2[1];

  if (fetching) {
    return /*#__PURE__*/_jsx(Wrap, {
      banner: banner,
      children: /*#__PURE__*/_jsx(CollectionLoading, {})
    });
  }

  if (error) {
    var event = new CustomEvent(OV_ALERT, {
      detail: TEXT.alert
    });
    var _window = window,
        dispatchEvent = _window.dispatchEvent;
    var graphQLErrors = error.graphQLErrors,
        networkError = error.networkError;
    dispatchEvent(event);
    console.error(networkError, graphQLErrors);

    if (Sentry) {
      Sentry.captureException(error);
    }

    return /*#__PURE__*/_jsx(Wrap, {
      banner: banner,
      children: /*#__PURE__*/_jsx(ErrorMessage, {
        message: "There was an error loading this collection. Please refresh and try again.",
        details: error
      })
    });
  }

  if (!data.collection) {
    window.location.assign('/404');
  }

  _toConsumableArray(document.querySelectorAll('.nav-collection-id')).forEach(function (link) {
    link.href = "".concat(link.getAttribute('data-url')).concat(data.collection.id);
  });

  var collection = data.collection;
  var paletteId = parseInt(paletteParam, 10);
  var id = collection.id,
      productSets = collection.productSets,
      properties = collection.properties,
      title = collection.title;
  var bottomComponent = properties.bottomComponent,
      fabrics = properties.breadcrumbDisplayType,
      collectionDescription = properties.collectionDescription,
      filters = properties.filters,
      footerComponent = properties.footerComponent,
      gridDisplayEditorial = properties.gridDisplayEditorial,
      gridDisplaySwatches = properties.gridDisplaySwatches,
      headerComponent = properties.headerComponent,
      recommendation = properties.recommendation; // If we are not requesting the colorways, set the faceout color

  if (!gridDisplaySwatches) {
    productSets.forEach(function (productSet) {
      productSet.products.edges.forEach(function (_ref3) {
        var _ref3$node = _ref3.node,
            component = _ref3$node.component,
            product = _ref3$node.product,
            faceout = _ref3$node.faceout;

        if (component) {
          return;
        }
        /* eslint-disable no-param-reassign */


        product.colorways = {
          edges: [{
            node: faceout
          }]
        };
      });
    });
  }

  productSets.forEach(function (productSet) {
    productSet.products.edges.forEach(function (_ref4) {
      var product = _ref4.node.product;
      if (product === null) return;
      product === null || product === void 0 ? void 0 : product.colorways.edges.forEach(function (_ref5) {
        var _node$sizesAvailable;

        var node = _ref5.node;
        (_node$sizesAvailable = node.sizesAvailable) === null || _node$sizesAvailable === void 0 ? void 0 : _node$sizesAvailable.forEach(function (size) {
          size.sizesSplit = size.size.replace(/\s/g, '').split(/[/-]/).reduce(function (acc, curr) {
            return acc[curr] = true, acc;
          }, {});
        });
      });
    });
  });

  if (gridDisplayEditorial) {
    // Loop through all of the images and see if there is an editorial match. If there is, swap the images
    productSets.forEach(function (_ref6) {
      var edges = _ref6.products.edges;
      return edges.forEach(function (_ref7) {
        var product = _ref7.node.product;
        product === null || product === void 0 ? void 0 : product.colorways.edges.forEach(function (_ref8) {
          var node = _ref8.node;

          if (node.assets.edges.length > 1 && node.assets.edges[1].node.isEditorial) {
            node.image = node.assets.edges[1].node.image;
          }
        });
      });
    });
  }

  var urlParams = new URLSearchParams(window.location.search);
  var filty = JSON.parse(filters);
  var initialFilters = filty.filters.map(function (_ref9) {
    var name = _ref9.name,
        type = _ref9.type,
        values = _ref9.values;
    return filtersMap[type]({
      label: name,
      values: values,
      urlParams: urlParams,
      paletteId: paletteId
    });
  });
  var drawerSection = {
    child: /*#__PURE__*/_jsx("div", {
      children: collectionDescription
    }),
    className: css.drawer,
    id: "".concat(id, "-description"),
    onClick: function onClick() {},
    title: 'Collection Details'
  };
  var collectionProps = {
    collection: collection,
    productSets: productSets,
    initialFilters: initialFilters
  };
  return /*#__PURE__*/_jsx(CollectionContext.Provider, {
    value: {
      fabrics: fabrics,
      gender: gender,
      setGender: setGender
    },
    children: /*#__PURE__*/_jsxs(Wrap, {
      banner: properties.banner,
      gender: gender,
      pageTitle: title,
      children: [headerComponent && /*#__PURE__*/_jsx(CollectionComponent, {
        component: headerComponent,
        collectionId: collection.id
      }), productSets.length === 1 && /*#__PURE__*/_jsx(Collection, _objectSpread({}, collectionProps)), productSets.length !== 1 && /*#__PURE__*/_jsx(CollectionSet, _objectSpread({}, collectionProps)), bottomComponent && /*#__PURE__*/_jsx(CollectionComponent, {
        component: bottomComponent,
        collectionId: collection.id
      }), footerComponent && /*#__PURE__*/_jsx(CollectionComponent, {
        component: footerComponent,
        collectionId: collection.id
      }), recommendation && /*#__PURE__*/_jsx(RecommendationsAI, _objectSpread({}, recommendation)), collectionDescription && /*#__PURE__*/_jsx("div", {
        className: css.expanderRoot,
        children: /*#__PURE__*/_jsx("div", {
          className: css.expander,
          children: /*#__PURE__*/_jsx(ExpandCollapser, {
            alignLeft: true,
            border: false,
            index: 0,
            sections: [drawerSection]
          })
        })
      })]
    })
  });
};

CollectionLoader.defaultProps = {
  banner: {
    component: 'TEXT_ONLY'
  },
  paletteParam: '',
  shopifyId: ''
};
CollectionLoader.propTypes = {
  banner: PropTypes.shape(),
  displaySwatches: PropTypes.bool.isRequired,
  paletteParam: PropTypes.string,
  shopifyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
export default CollectionLoader;